import i18n from '@i18n/index';

/**
 * Get translation text outside of React components
 * @param key - Translation key (e.g., 'common.errors.unauthorized')
 * @param options - Translation options
 * @returns Translated text
 * 
 * @example
 * // Basic usage
 * const message = getTranslation('common.errors.unauthorized');
 * 
 * // With interpolation
 * const message = getTranslation('common.welcome', { name: 'John' });
 */
export const getTranslation = (key: string, options?: Record<string, unknown>): string => {
  return i18n.t(key, options) as string;
};

/**
 * Get current language
 * @returns Current language code
 */
export const getCurrentLanguage = (): string => {
  return i18n.language;
};

/**
 * Change language programmatically
 * @param language - Language code to change to
 */
export const changeLanguage = (language: string): void => {
  i18n.changeLanguage(language);
  localStorage.setItem("preferredLanguage", language);
};

/**
 * Check if i18n is ready
 * @returns Boolean indicating if i18n is initialized
 */
export const isI18nReady = (): boolean => {
  return i18n.isInitialized;
};

/**
 * Add language change listener
 * @param callback - Function to call when language changes
 * @returns Function to remove the listener
 * 
 * @example
 * const removeListener = addLanguageChangeListener((language) => {
 *   console.log('Language changed to:', language);
 *   // Update any cached translations or re-fetch data
 * });
 * 
 * // Later, remove the listener
 * removeListener();
 */
export const addLanguageChangeListener = (callback: (language: string) => void): (() => void) => {
  const handleLanguageChanged = (lng: string) => {
    callback(lng);
  };
  
  i18n.on('languageChanged', handleLanguageChanged);
  
  // Return function to remove listener
  return () => {
    i18n.off('languageChanged', handleLanguageChanged);
  };
};

// /**
//  * Get translation with fallback
//  * @param key - Translation key
//  * @param fallback - Fallback text if translation not found
//  * @param options - Translation options
//  * @returns Translated text or fallback
//  * 
//  * @example
//  * const message = getTranslationWithFallback(
//  *   'common.errors.customError',
//  *   'An error occurred',
//  *   { errorCode: 500 }
//  * );
//  */
// export const getTranslationWithFallback = (key: string, fallback: string, options?: any): string => {
//   const translation = i18n.t(key, options) as string;
//   return translation === key ? fallback : translation;
// };

/**
 * Check if a translation key exists
 * @param key - Translation key to check
 * @returns Boolean indicating if key exists
 */
export const hasTranslation = (key: string): boolean => {
  return i18n.exists(key);
};

/**
 * Get all available languages
 * @returns Array of available language codes
 */
export const getAvailableLanguages = (): string[] => {
  return Object.keys(i18n.options.resources || {});
};

/**
 * Get translation with pluralization
 * @param key - Translation key
 * @param count - Count for pluralization
 * @param options - Additional options
 * @returns Translated text with proper pluralization
 * 
 * @example
 * const message = getTranslationWithPlural('common.items', 5);
 * // Returns "5 items" in English or appropriate plural form in other languages
 */
// export const getTranslationWithPlural = (key: string, count: number, options?: any): string => {
//   return i18n.t(key, { count, ...options }) as string;
// }; 